import type { CanvaSXObject, MountedObject } from '@lib/canvasx/canvasx';
import type { fabric } from 'fabric';
import type { PlainControlBase as ControlBase } from '../fabric/ControlBase';
import { type ControlMouseEventHandler } from 'fabric/fabric-impl';
import type Canvas from '../objects/Canvas';
import { renderTooltip } from '../objects/WhiteboardTooltip';
import type { Constructor, Rect } from '../types';
import { pointInRect } from '../utils/geometry';

export type CanvaSXObjectState = CanvaSXObject & {
  prevMountedObjectKey: string | null;
  hoverTimeouts: Map<string, ReturnType<typeof setTimeout>>;
  hoverTooltips: Map<string, ReturnType<typeof renderTooltip>>;
};

// eslint-disable-next-line @typescript-eslint/no-redeclare
function ControlCanvaSXBehaviors(Base: Constructor<ControlBase>) {
  return class WithControlCanvaSXBehaviors extends Base {
    initialObjectState(): CanvaSXObjectState {
      return {
        ...super.initialObjectState(),
        mountedObjects: {},
        prevMountedObjectKey: null,
        hoverTimeouts: new Map(),
        hoverTooltips: new Map(),
      };
    }

    getState(
      fabricObject: fabric.Object
    ): ReturnType<typeof this.initialObjectState> {
      return super.getState(fabricObject) as CanvaSXObjectState;
    }

    getMouseUpHandler(
      eventData: MouseEvent,
      fabricObject: fabric.Object,
      control: ControlBase
    ): ControlMouseEventHandler {
      const previous = super.getMouseUpHandler(
        eventData,
        fabricObject,
        control
      );

      const mountedObject = this.findMountedObject(eventData, fabricObject);
      if (mountedObject?.onClick) {
        const { onClick } = mountedObject;
        return (eventData, transformData, x, y) => {
          onClick(eventData);

          if (previous) {
            return previous(eventData, transformData, x, y);
          } else {
            return true;
          }
        };
      }

      return previous;
    }

    showTooltip(state: CanvaSXObjectState, key: string, canvas: Canvas): void {
      const mountedObject = state.mountedObjects[key];
      if (!mountedObject) return;

      let content: React.ReactNode;

      if (typeof mountedObject.tooltip === 'function') {
        content = mountedObject.tooltip(key);
      } else {
        content = mountedObject.tooltip;
      }

      const rect = {
        left: mountedObject.x,
        top: mountedObject.y,
        width: mountedObject.width,
        height: mountedObject.height,
      };

      const tooltipCoords = rect;

      if (!tooltipCoords) return;

      const tooltip = state.hoverTooltips.get(key);
      if (tooltip) tooltip();

      state.hoverTooltips.set(
        key,
        renderTooltip(content, tooltipCoords, canvas)
      );
      canvas.requestRenderAll();
    }

    delayShowTooltip(
      state: CanvaSXObjectState,
      key: string,
      canvas: Canvas
    ): void {
      const mountedObject = state.mountedObjects[key];

      if (!mountedObject?.tooltip) return;
      if (!state.hoverTimeouts.has(key) && !state.hoverTooltips.has(key)) {
        state.hoverTimeouts.set(
          key,
          setTimeout(() => {
            this.showTooltip(state, key, canvas);
          }, 500)
        );
      }
    }

    hideTooltip(state: CanvaSXObjectState, key: string, canvas: Canvas): void {
      if (state.hoverTimeouts.has(key)) {
        clearTimeout(state.hoverTimeouts.get(key));
        state.hoverTimeouts.delete(key);
      }

      const tooltip = state.hoverTooltips.get(key);

      if (tooltip) {
        tooltip();
        state.hoverTooltips.delete(key);
        canvas.requestRenderAll();
      }
    }

    onMountedObjectMouseOut(
      eventData: MouseEvent,
      fabricObject: fabric.Object,
      key: string
    ): void {
      const state = this.getState(fabricObject);
      const mountedObject = state.mountedObjects[key];
      if (mountedObject) {
        this.hideTooltip(state, key, fabricObject.canvas);

        const { onMouseOut } = mountedObject;
        if (onMouseOut) {
          onMouseOut(eventData);
        }
      }
    }

    onMountedObjectMouseOver(
      eventData: MouseEvent,
      fabricObject: fabric.Object,
      key: string
    ): void {
      const state = this.getState(fabricObject);
      const mountedObject = state.mountedObjects[key];
      if (mountedObject) {
        this.delayShowTooltip(state, key, fabricObject.canvas);

        const { onMouseOver } = mountedObject;
        if (onMouseOver) {
          onMouseOver(eventData);
        }
      }
    }

    onMouseMove(eventData: MouseEvent, fabricObject: fabric.Object): void {
      const state = this.getState(fabricObject);
      const mountedObjectKey = this.findKeyForEvent(eventData, fabricObject);

      if (mountedObjectKey !== state.prevMountedObjectKey) {
        if (state.prevMountedObjectKey) {
          this.onMountedObjectMouseOut(
            eventData,
            fabricObject,
            state.prevMountedObjectKey
          );
        }
        if (mountedObjectKey) {
          this.onMountedObjectMouseOver(
            eventData,
            fabricObject,
            mountedObjectKey
          );
        }
      }

      if (mountedObjectKey) {
        const mountedObject = state.mountedObjects[mountedObjectKey];
        if (mountedObject) {
          const { onMouseMove } = mountedObject;
          if (onMouseMove) {
            onMouseMove(eventData);
          }
        }
      }

      state.prevMountedObjectKey = mountedObjectKey ?? null;
      super.onMouseMove(eventData, fabricObject);
    }

    onMouseOut(eventData: MouseEvent, fabricObject: fabric.Object): void {
      const state = this.getState(fabricObject);
      if (state.prevMountedObjectKey) {
        this.onMountedObjectMouseOut(
          eventData,
          fabricObject,
          state.prevMountedObjectKey
        );
      }
      state.prevMountedObjectKey = null;

      super.onMouseOut(eventData, fabricObject);
    }

    cursorStyleHandler(
      eventData: MouseEvent,
      control: WithControlCanvaSXBehaviors,
      fabricObject: fabric.Object
    ): string {
      const mountedObject = control.findMountedObject(eventData, fabricObject);
      if (mountedObject) {
        const { cursor, onClick } = mountedObject;
        if (cursor) {
          return cursor;
        } else if (onClick) {
          return 'pointer';
        }
      }

      return super.cursorStyleHandler(eventData, control, fabricObject);
    }

    findMountedObject(
      eventData: Event,
      fabricObject: fabric.Object
    ): MountedObject | undefined {
      const state = this.getState(fabricObject);
      const key = this.findKeyForEvent(eventData, fabricObject);

      if (key) {
        return state.mountedObjects[key];
      }
    }

    findKeyForEvent(
      eventData: Event,
      fabricObject: fabric.Object
    ): string | undefined {
      const state = this.getState(fabricObject);
      if (!state.mountedObjects) {
        return;
      }

      const pointer = fabricObject.canvas.getPointer(eventData, true);
      return Object.keys(state.mountedObjects).find(key => {
        const { x, y, width, height } = state.mountedObjects[key];
        return pointInRect(pointer, { left: x, top: y, width, height });
      });
    }

    getMountedObjectRect(
      key: string,
      fabricObject: fabric.Object
    ): Rect | undefined {
      const state = this.getState(fabricObject);
      const object = state.mountedObjects[key];
      if (!object) return;

      return {
        left: object.x,
        top: object.y,
        width: object.width,
        height: object.height,
      };
    }
  };
}

export default ControlCanvaSXBehaviors;
